<template>
  <div
    v-if="!hasPermission('CanSubmitExpressReturns') && !hasPermission('CanSubmitAnnualReturns')"
    class="container create-return cr"
  >
    <b-row>
      <b-col>
        <not-authorized />
      </b-col>
    </b-row>
  </div>
  <div v-else-if="pageIsLoading" class="cr__loading">
    <loading-spinner class="cr__loading-spinner" />
  </div>
  <div v-else-if="!pageIsLoading && IsMcfa && mlaReturnBlackoutIsActive" class="container">
    <div class="col">
      <div class="row">
        <div v-html="mlaReturnBlackoutHtml"></div>
      </div>
    </div>
  </div>
  <div v-else-if="!pageIsLoading && !isSubmitting" id="CreateReturn" class="container create-return cr">
    <div class="col">
      <div class="row">
        <h1>Create Return</h1>
      </div>
    </div>
    <div id="returnsStepper">
      <v-stepper ref="stepper" v-model="currentReturnStep" :steps="returnSteps" :with-divider="false">
        <template slot="step-1"> Start Return </template>
        <template slot="step-2"> Select Parts </template>
        <template slot="step-3"> Return Info </template>
        <template slot="step-4"> Review &amp; Submit </template>
      </v-stepper>
      <template v-if="currentReturnStep === 1">
        <CreateReturnSelectType :return-draft-dto="returnDraftDto" :validation-state="returnStepsValidState" />
      </template>
      <template v-if="currentReturnStep === 2">
        <CreateReturnSelectParts :return-draft-dto="returnDraftDto" :validation-state="returnStepsValidState" />
      </template>
      <template v-if="currentReturnStep === 3">
        <CreateReturnReturnInfo :contact-info="returnDraftDto" :validation-state="returnStepsValidState" />
      </template>
      <template v-if="currentReturnStep === 4">
        <CreateReturnReviewSubmit :return-draft-dto="returnDraftDto" />
      </template>
    </div>

    <!-- Forward Next -->
    <div class="container-fluid no-gutters">
      <div class="col">
        <div class="row">
          <div class="px-4 bg-light pb-4 border-bottom col-lg-12 col-sm-12">
            <DnFunctionButton
              v-if="!allReturnsBlackedOut"
              button-id="nextOrSubmitBtn"
              :text="NextOrSubmitBtnText"
              :callback-fn="TryNextOrSubmit"
              extra-classes="pull-right"
              :size="largeSize"
              :loading="isLoading"
            />
            <DnFunctionButton
              v-if="currentReturnStep >= 2"
              button-id="backBtn"
              extra-classes="pull-right mr-3"
              :callback-fn="Previous"
              text="Back"
              :size="largeSize"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- CONFIRMATION DIALOGS -->
  <div v-else-if="!pageIsLoading && isSubmitting" class="container create-return cr">
    <!-- CONFIRM -->
    <div v-show="isConfirming" class="text-center">
      <h3>Are you sure you want to submit your return?</h3>
      <button id="ConfirmReturn-btn" class="btn btn-smallOrangeButton" @click="ConfirmReturn">
        {{ getContentItem("submit") }}
      </button>
    </div>

    <!-- SUBMITTING -->
    <div v-show="isSubmittingReturn">
      <div class="row no-gutters justify-content-center cr__submitting-message">
        <h3>Submission in progress...&nbsp;</h3>
        <i class="element-loading cr__submitting-spinner" />
      </div>
    </div>

    <!-- CONFIRMED -->
    <div v-show="isConfirmed" class="cr__return-submitted">
      <h3>The following return(s) were created:</h3>
      <div
        v-for="(returnReference, returnResults) in returnPlacementResultDto"
        :key="returnResults"
        class="cr__return-submitted-list"
      >
        <div class="row no-gutters">
          <div class="col">
            <ul>
              <li>{{ returnReference.ReturnReferenceNumber }}</li>
            </ul>
          </div>
        </div>
      </div>
      <button id="ConfirmReturn" class="btn btn-smallOrangeButton" @click="OnSubmitSuccess">
        {{ getContentItem("Ok") }}
      </button>
    </div>

    <!-- ERRORS IN RETURN -->
    <div v-show="hasErrors">
      <h3>The return submission failed with the following errors:</h3>
      <div v-for="(returnReference, returnResults) in returnPlacementResultDto" :key="returnResults">
        <div v-if="returnReference.Errors && returnReference.Errors.length" class="row no-gutters">
          <div class="col">
            <div class="row no-gutters">Return Level Errors:</div>
            <div class="row no-gutters">
              <ul v-for="(currentError, returnErrors) in returnReference.Errors" :key="returnErrors">
                <li>{{ currentError }}</li>
              </ul>
            </div>
          </div>
        </div>
        <div
          v-if="returnReference.ItemErrors && Object.entries(returnReference.ItemErrors).length"
          class="row no-gutters"
        >
          <div class="col">
            <div class="row no-gutters">Item Level Errors:</div>
            <div class="row no-gutters">
              <ul
                v-for="(currentItemKey, returnItemErrors) in Object.keys(returnReference.ItemErrors)"
                :key="returnItemErrors"
              >
                <li>{{ currentItemKey }} : {{ returnReference.ItemErrors[currentItemKey] }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <button id="ReturnsError" class="btn btn-smallOrangeButton" @click="CloseErrors">
        {{ getContentItem("close") }}
      </button>
    </div>

    <!-- ERROR SUBMITTING RETURN-->
    <div v-show="errorSubmitting" class="text-center">
      <h3>Error Submitting Return</h3>
      <p><em>An error has occurred. Please reload the page or try again later.</em></p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { VStepper } from "vue-stepper-component";
import { sizeHelper } from "@/components/mixins/sizeHelper";
import DnFunctionButton from "@/components/Elements/DnFunctionButton";
import CreateReturnReturnInfo from "./CreateReturnReturnInfo";
import CreateReturnReviewSubmit from "./CreateReturnReviewSubmit";
import CreateReturnSelectParts from "./CreateReturnSelectParts";
import CreateReturnSelectType from "./CreateReturnSelectType";
import { ReturnsService } from "@/services/ReturnsService";
import { ErrorLogService } from "@/services/ErrorLogService";
import { SettingsService } from "@/services/SettingsService";
import NotAuthorized from "@/components/Elements/NotAuthorized";
import { constants } from "@/const";
import { faLifeRing } from "@fortawesome/free-solid-svg-icons";
import { LoadingSpinner } from "@/components";

export default {
  components: {
    VStepper,
    DnFunctionButton,
    CreateReturnReturnInfo,
    CreateReturnReviewSubmit,
    CreateReturnSelectParts,
    CreateReturnSelectType,
    NotAuthorized,
    LoadingSpinner,
  },
  mixins: [sizeHelper],
  props: {
    returnId: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    currentReturnStep: 1,
    returnSteps: 4,
    returnDraftDto: {
      Id: Number,
      ContactName: String,
      ContactPhone: String,
      ContactEmail: String,
      PreferredContactMethod: "Email",
      ActionForNonReturnable: "Blank",
      ReferenceNumber: "",
      ReturnDraftType: "",
      AccountId: Number,
      Items: Object,
      SubtotalMcf: Number,
      McfReturnAmountRemaining: Number,
      SubtotalRapidparts: Number,
      RpReturnAmountRemaining: Number,
      MinimumReturnAmount: Number,
      MinimumItemAmount: Number,
    },
    returnPlacementResultDto: {},
    returnStepsValidState: {
      stepOne: false,
      stepTwo: false,
      stepThree: false,
      stepFour: false,
    },
    isLoading: false,
    isSubmitting: false,
    isSubmittingReturn: false,
    isConfirming: false,
    isConfirmed: false,
    hasErrors: false,
    errorSubmitting: false,
    mlaReturnBlackoutIsActive: false,
    mlaReturnBlackoutHtml: "",
    allReturnsBlackedOut: false,
    pageIsLoading: true,
  }),
  computed: {
    ...mapGetters(["activeAccountId", "user", "hasPermission", "getContentItem", "IsMcfa", "McfCostCenterId"]),

    NextOrSubmitBtnText: function () {
      if (this.currentReturnStep === 4) {
        return "Submit";
      } else {
        return "Next";
      }
    },
  },
  mounted() {
    this.pageIsLoading = true;
    if (this.IsMcfa) {
      SettingsService.getAllSettings().then((response) => {
        var settings = response;
        var settingKeys = constants.MLA_RETURNS_BLACK_SETTINGS_KEYS;

        var blackoutActiveFlagSetting = _.find(settings, (x) => x.Key == settingKeys.ACTIVE);
        var startDateSetting = _.find(settings, (x) => x.Key == settingKeys.START_DATE);
        var endDateSetting = _.find(settings, (x) => x.Key == settingKeys.END_DATE);
        var messageHtmlSetting = _.find(settings, (x) => x.Key == settingKeys.MESSAGE);

        var blackoutActiveFlag = blackoutActiveFlagSetting.Value;
        var startDateString = startDateSetting.Value;
        var endDateString = endDateSetting.Value;
        var messageHtml = messageHtmlSetting.Value;

        var flagIsActive = blackoutActiveFlag && blackoutActiveFlag.trim().toLowerCase() == "true";
        var startDate = new Date(startDateString);
        var endDate = new Date(endDateString);
        var now = new Date();
        var inDateRange = startDate && endDate && now >= startDate && now <= endDate;
        this.mlaReturnBlackoutIsActive = flagIsActive && inDateRange;

        if (this.mlaReturnBlackoutIsActive) {
          this.mlaReturnBlackoutHtml = messageHtml;
        } else {
          this.Load();
        }
        this.pageIsLoading = false;
      });
      ReturnsService.GetBlackoutStatus(this.McfCostCenterId).then((blackoutStatus) => {
        this.allReturnsBlackedOut = blackoutStatus.data.SurplusReturnsBlackedOut &&
          blackoutStatus.data.ExpressReturnsBlackedOut;
      });
    } else {
      this.pageIsLoading = false;
      this.Load();
    }
  },
  methods: {
    Load() {
      this.SetContactInfo();
      if (this.returnId > 0) {
        ReturnsService.GetDraftReturn(this.activeAccountId, this.user.UserId, this.returnId).then((returnDraftDto) => {
          if (returnDraftDto.data != null) {
            this.returnDraftDto = returnDraftDto.data;
          }
          this.currentReturnStep = 2;
        });
      } else if (this.returnId === 0) {
        this.$router.replace({ path: "/claimsAndReturns/createReturn" });
      }
    },
    TryNextOrSubmit() {
      this.isLoading = true;
      let validationState;
      switch (this.currentReturnStep) {
        case 1:
          validationState = this.returnStepsValidState.stepOne;
          break;
        case 2:
          validationState = this.returnStepsValidState.stepTwo;
          break;
        case 3:
          validationState = this.returnStepsValidState.stepThree;
          break;
        case 4:
          this.isSubmitting = true;
          this.isConfirming = true;
          break;
        default:
          this.$refs.stepper.next();
          break;
      }

      let self = this;

      if (validationState) {
        ReturnsService.SaveDraft(this.activeAccountId, this.user.UserId, this.returnDraftDto).then((saveResult) => {
          if (saveResult.data != null) {
            self.returnDraftDto = saveResult.data;
          }
          self.isLoading = false;
          self.$store.commit("removeAllMessages");
          this.$refs.stepper.next();
        });
      } else {
        this.isLoading = false;
      }
    },
    Previous() {
      this.$store.commit("removeAllMessages");
      this.$refs.stepper.previous();
    },
    SetContactInfo() {
      this.returnDraftDto.ContactName = this.user.FullName;
      this.returnDraftDto.ContactPhone = this.user.Phone;
      this.returnDraftDto.ContactEmail = this.user.Email;
    },
    ConfirmReturn() {
      this.isConfirming = false;
      this.isSubmittingReturn = true;

      let self = this;
      let forceSubmission = this.hasPermission("IgnoreReturnValidation");
      ReturnsService.SubmitReturn(this.activeAccountId, this.user.UserId, this.returnDraftDto.Id, forceSubmission)
        .then((returnResponse) => {
          let responseDto = [];
          let responseErrors = false;
          for (let i = 0; i < returnResponse.data.length; i++) {
            if (!returnResponse.data[i].Success) {
              responseErrors = true;
            }

            responseDto.push({
              ReturnReferenceNumber: returnResponse.data[i].ReturnReferenceNumber,
              Errors: returnResponse.data[i].Errors,
              ItemErrors: returnResponse.data[i].ItemErrors,
              Success: returnResponse.data[i].Success,
            });
          }

          self.returnPlacementResultDto = responseDto;
          if (responseErrors) {
            this.isSubmittingReturn = false;
            this.hasErrors = true;
          } else {
            this.isSubmittingReturn = false;
            this.isConfirmed = true;
          }
        })
        .catch((err) => {
          ErrorLogService.LogException(err);
          this.isSubmittingReturn = false;
          this.submitBombed = true;
        });
    },
    OnSubmitSuccess() {
      this.$router.push({
        name: "ViewReturns",
      });
    },
    CloseErrors() {
      this.isSubmitting = false;
      this.hasErrors = false;
    },
  },
};
</script>

<style lang="scss">
.create-return {
  margin-top: 80px;
}

.cr__submitting-spinner {
  padding: 0;
  margin-top: 4px;
}

.cr__return-submitted {
  text-align: center;
}

.cr__return-submitted-list {
  list-style-position: inside;
}

.cr__loading {
  padding: 12px;
  text-align: center;
}
.cr__loading-spinner {
  margin: auto;
  margin-bottom: 0;
}
</style>